import type { Meta } from '@brighterly/lib-core-types/api/interfaces'
import type { AppBadgeColor } from '@shared/ui/badges'
import type { AssessmentTemplateListRow } from './interfaces'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import { useDayjs } from '@shared/lib/format'
import { DEFAULT_APP_TABLE_META } from '@shared/ui/tables'
import { ROWS_PER_PAGE_OPTIONS } from '@shared/ui/paginations'

import { useAssessmentTemplatesApi } from '../../api/assessment-templates/list.hook'
import { useAssessmentTemplatesStore } from '../assessment-templates/store'
import {
  getAssessmentEditPath,
  getAssessmentTypeLabel,
  getDisciplineCodeLabel,
  getGradeLabel,
  getTemplateStatusColor,
  getTemplateStatusLabel,
} from './assessment-template-options'

interface TableAssessmentTemplatesReturn {
  templates: ComputedRef<AssessmentTemplateListRow[]>
  isLoading: ComputedRef<boolean>
  hasTemplates: ComputedRef<boolean>
  meta: ComputedRef<Meta>
  perPageOptions: ComputedRef<number[]>
  formatDateTime: (value: string | null) => string
  getTypeLabel: (template: AssessmentTemplateListRow) => string
  getDisciplineLabel: (template: AssessmentTemplateListRow) => string
  getGrade: (template: AssessmentTemplateListRow) => string
  getStatusLabel: (template: AssessmentTemplateListRow) => string
  getStatusColor: (template: AssessmentTemplateListRow) => AppBadgeColor
  openTemplate: (template: AssessmentTemplateListRow) => Promise<void>
  setChangePage: (value: number) => Promise<void>
  setChangePerPage: (value: number) => Promise<void>
}

export const useTableAssessmentTemplates = (): TableAssessmentTemplatesReturn => {
  const router = useRouter()
  const route = useRoute()
  const store = useAssessmentTemplatesStore()
  const dayjs = useDayjs()

  const templates = computed(() => store.getTemplates)
  const isLoading = computed(() => store.isLoading)
  const hasTemplates = computed(() => store.hasTemplates)
  const meta = computed<Meta>(() => store.getMeta ?? DEFAULT_APP_TABLE_META)

  /**
   * `?per_page=9999` is clamped to the contract's ceiling of 500, a legitimate value the standard
   * option list does not offer — the select would render blank while the request carried 500. The
   * current value is added to the list so the control cannot contradict what was requested.
   */
  const perPageOptions = computed<number[]>(() => {
    const current = meta.value.per_page
    if (!current || ROWS_PER_PAGE_OPTIONS.includes(current)) return ROWS_PER_PAGE_OPTIONS
    return [...ROWS_PER_PAGE_OPTIONS, current].sort((a, b) => a - b)
  })

  const formatDateTime = (value: string | null): string =>
    value ? dayjs(value).format(DateFormat.KanbanDate) : '—'

  const getTypeLabel = (template: AssessmentTemplateListRow): string =>
    getAssessmentTypeLabel(template.type)

  const getDisciplineLabel = (template: AssessmentTemplateListRow): string =>
    getDisciplineCodeLabel(template.discipline_code)

  const getGrade = (template: AssessmentTemplateListRow): string => getGradeLabel(template.grade)

  const getStatusLabel = (template: AssessmentTemplateListRow): string =>
    getTemplateStatusLabel(template.status)

  const getStatusColor = (template: AssessmentTemplateListRow): AppBadgeColor =>
    getTemplateStatusColor(template.status)

  /** The editor route. It 404-renders until the editor page lands — expected. */
  const openTemplate = async (template: AssessmentTemplateListRow): Promise<void> => {
    await router.push(getAssessmentEditPath(template.id))
  }

  /**
   * Nothing writes the response's `current_page` back into the store, so the requested page is set
   * here as well as pushed onto the query — the list hook reads the query first, and the store
   * field is what a later request falls back to.
   */
  const setChangePage = async (value: number): Promise<void> => {
    store.setCurrentPage(value)
    await router.push({
      query: {
        ...route.query,
        page: String(value),
      },
    })
    await useAssessmentTemplatesApi()
  }

  const setChangePerPage = async (value: number): Promise<void> => {
    store.setPerPage(value)
    store.setCurrentPage(1)
    await router.push({
      query: {
        ...route.query,
        per_page: String(value),
        page: undefined,
      },
    })
    await useAssessmentTemplatesApi()
  }

  return {
    templates,
    isLoading,
    hasTemplates,
    meta,
    perPageOptions,
    formatDateTime,
    getTypeLabel,
    getDisciplineLabel,
    getGrade,
    getStatusLabel,
    getStatusColor,
    openTemplate,
    setChangePage,
    setChangePerPage,
  }
}
